import { apiClient, unwrap } from './client';

import type {
  ApiResponse,
  ClassAnnouncementItem,
  ClassMaterialItem,
  ClassroomOut,
  LeaderboardResponse,
} from '@/types';

export async function createClass(payload: {
  name: string;
  description?: string;
}): Promise<ClassroomOut> {
  const fd = new FormData();
  fd.append('name', payload.name);
  fd.append('description', payload.description || '');
  const response = await apiClient.post<ApiResponse<ClassroomOut>>('/classes', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(response);
}

export async function listMyClasses(): Promise<ClassroomOut[]> {
  const response = await apiClient.get<ApiResponse<ClassroomOut[]>>('/classes');
  return unwrap(response);
}

export async function getClassDetail(id: number): Promise<ClassroomOut> {
  const response = await apiClient.get<ApiResponse<ClassroomOut>>(`/classes/${id}`);
  return unwrap(response);
}

export async function previewClass(code: string): Promise<ClassroomOut> {
  const response = await apiClient.get<ApiResponse<ClassroomOut>>(
    `/classes/by-code/${code}`,
  );
  return unwrap(response);
}

export async function joinClass(code: string): Promise<ClassroomOut> {
  const fd = new FormData();
  fd.append('code', code);
  const response = await apiClient.post<ApiResponse<ClassroomOut>>('/classes/join', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(response);
}

export async function listJoinedClasses(): Promise<ClassroomOut[]> {
  const response = await apiClient.get<ApiResponse<ClassroomOut[]>>('/classes/joined');
  return unwrap(response);
}

export async function leaveClass(id: number): Promise<{ ok: boolean }> {
  const response = await apiClient.post<ApiResponse<{ ok: boolean }>>(
    `/classes/${id}/leave`,
  );
  return unwrap(response);
}

export async function deleteClass(id: number): Promise<{ ok: boolean }> {
  const response = await apiClient.delete<ApiResponse<{ ok: boolean }>>(`/classes/${id}`);
  return unwrap(response);
}

export async function updateClass(
  id: number,
  payload: { name: string; description?: string },
): Promise<ClassroomOut> {
  const fd = new FormData();
  fd.append('name', payload.name);
  fd.append('description', payload.description || '');
  const response = await apiClient.put<ApiResponse<ClassroomOut>>(`/classes/${id}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(response);
}

export async function archiveClass(id: number, archived: boolean): Promise<ClassroomOut> {
  const fd = new FormData();
  fd.append('archived', String(archived));
  const response = await apiClient.post<ApiResponse<ClassroomOut>>(
    `/classes/${id}/archive`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap(response);
}

export async function removeStudent(
  classId: number,
  studentId: number,
): Promise<{ ok: boolean }> {
  const response = await apiClient.delete<ApiResponse<{ ok: boolean }>>(
    `/classes/${classId}/students/${studentId}`,
  );
  return unwrap(response);
}

// ---------- 多教师：协作加入 ----------

export async function teacherJoinClass(code: string): Promise<ClassroomOut> {
  const fd = new FormData();
  fd.append('code', code);
  const response = await apiClient.post<ApiResponse<ClassroomOut>>(
    '/classes/teacher-join',
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap(response);
}

// ---------- 班级公告 ----------

export async function listAnnouncements(classId: number): Promise<ClassAnnouncementItem[]> {
  const response = await apiClient.get<ApiResponse<ClassAnnouncementItem[]>>(
    `/classes/${classId}/announcements`,
  );
  return unwrap(response);
}

export async function createAnnouncement(
  classId: number,
  payload: { title: string; content: string },
): Promise<ClassAnnouncementItem> {
  const fd = new FormData();
  fd.append('title', payload.title);
  fd.append('content', payload.content);
  const response = await apiClient.post<ApiResponse<ClassAnnouncementItem>>(
    `/classes/${classId}/announcements`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap(response);
}

export async function deleteAnnouncement(
  classId: number,
  announcementId: number,
): Promise<{ ok: boolean }> {
  const response = await apiClient.delete<ApiResponse<{ ok: boolean }>>(
    `/classes/${classId}/announcements/${announcementId}`,
  );
  return unwrap(response);
}

// ---------- 班级排行榜 ----------

export async function getLeaderboard(classId: number): Promise<LeaderboardResponse> {
  const response = await apiClient.get<ApiResponse<LeaderboardResponse>>(
    `/classes/${classId}/leaderboard`,
  );
  return unwrap(response);
}

// ---------- 班级资料区 ----------

export async function listMaterials(classId: number): Promise<ClassMaterialItem[]> {
  const response = await apiClient.get<ApiResponse<ClassMaterialItem[]>>(
    `/classes/${classId}/materials`,
  );
  return unwrap(response);
}

export async function uploadMaterial(
  classId: number,
  payload: { title: string; description: string; file: File },
): Promise<ClassMaterialItem> {
  const fd = new FormData();
  fd.append('title', payload.title);
  fd.append('description', payload.description);
  fd.append('file', payload.file);
  const response = await apiClient.post<ApiResponse<ClassMaterialItem>>(
    `/classes/${classId}/materials`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap(response);
}

export async function deleteMaterial(
  classId: number,
  materialId: number,
): Promise<{ ok: boolean }> {
  const response = await apiClient.delete<ApiResponse<{ ok: boolean }>>(
    `/classes/${classId}/materials/${materialId}`,
  );
  return unwrap(response);
}
